import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import './ReportSourcesView.css'; // Reuse existing table CSS
import './WebSearchView.css'; // Import WebSearchView specific CSS
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useNavigate } from 'react-router-dom';
import env from './utils/env';

// Define the interface for the SearchResult data structure
export interface SearchResult {
    _id: string;
    url: string;
    index: number;
    query: string;
    update_timestamp: string | null;
    target_id: string[]; // Array of string IDs
    checked_on_pdf_timestamp: string | null;
    pdf_urls: string[];
    processed: boolean | number;
    insert_timestamp: string | null;
    cusip: string | null;
    has_isin: boolean;
    isin: string | null;
    validated: boolean;
    prepare_pdf_urls: string[];
    checking_failed: boolean;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ApiResponse {
    searchResults: SearchResult[];
    pagination?: PaginationMetadata;
}

// Define the interface for a new search target
interface NewSearchTarget {
    issuer: string;
    name: string;
    query: string;
    max_number_results: number;
    country: string;
    type?: string;
    source?: string;
}

// List of countries for dropdown
const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", 
    "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", 
    "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", 
    "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", 
    "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", 
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", 
    "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", 
    "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", 
    "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", 
    "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", 
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", 
    "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", 
    "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", 
    "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", 
    "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", 
    "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", 
    "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", 
    "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", 
    "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", 
    "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", 
    "Yemen", "Zambia", "Zimbabwe", "Supranational"
];

// Helper function to format target ID display
const formatTargetIdDisplay = (targetIds: string[]): string => {
    if (!targetIds || targetIds.length === 0) {
        return 'None';
    } else if (targetIds.length === 1) {
        return '1';
    } else {
        return `${targetIds.length}`;
    }
};

const WebSearchView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const navigate = useNavigate();
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    
    // Search form state
    const [newTarget, setNewTarget] = useState<NewSearchTarget>({
        issuer: '',
        name: '',
        query: '',
        max_number_results: 100,
        country: '',
        type: 'GENERIC_SEARCH',
        source: 'UI'
    });
    
    // Search results state
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);

    const [currentSearchTargetId, setCurrentSearchTargetId] = useState<string | null>(null);
    const limit = 50; // Number of items per page
    
    // State for details popup
    const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

    // Handler for input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewTarget(prev => ({
            ...prev,
            [name]: name === 'max_number_results' ? parseInt(value) || 100 : value
        }));
    };

    // Handler to perform a new search
    const handleSearch = async () => {
        // Validate inputs - only query is required
        if (!newTarget.query.trim()) {
            alert('Query field is required');
            return;
        }
        
        if (newTarget.max_number_results < 1) {
            alert('Max number of results must be at least 1');
            return;
        }
        
        setLoading(true);
        
        const headers: Record<string, string> = {
            'x-api-key': apiKey
        };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        try {
            // First create the search target
            const createResponse = await axios.post(`${apiUrl}/search-targets`, newTarget, {
                headers
            });
            
            const createdTargetId = createResponse.data._id;
            setCurrentSearchTargetId(createdTargetId);
            
            // Then fetch the search results for this target
            fetchSearchResults(1, [createdTargetId]);
            
        } catch (error) {
            console.error('Error performing search:', error);
            alert('Failed to perform search');
            setLoading(false);
        }
    };

    // Fetch search results for a specific target
    const fetchSearchResults = async (page: number, targetIds?: string[]) => {
        if (!authLoaded) return;
        
        setLoading(true);
        
        // Use provided targetIds or current search target
        const searchTargetIds = targetIds || (currentSearchTargetId ? [currentSearchTargetId] : []);
        
        if (searchTargetIds.length === 0) {
            setLoading(false);
            return;
        }
        
        // Construct the URL with appropriate query parameters
        let url = `${apiUrl}/search-results?limit=${limit}&page=${page}`;
        
        // Add target_id parameters
        searchTargetIds.forEach(id => {
            url += `&target_id=${encodeURIComponent(id)}`;
        });
        
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        try {
            const response = await axios.get<ApiResponse>(url, { headers });
            
            if (Array.isArray(response.data.searchResults)) {
                setSearchResults(response.data.searchResults);
            } else {
                console.error('Search results is not an array:', response.data.searchResults);
                setSearchResults([]);
            }
            
            // Set pagination metadata if available
            if (response.data.pagination) {
                setPagination(response.data.pagination);
            }
            
        } catch (error) {
            console.error('Error fetching search results:', error);
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        fetchSearchResults(newPage);
    };

    // Format date if it exists
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return new Intl.DateTimeFormat('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (error) {
            console.warn(`Date parsing failed: ${dateString}`, error);
            return dateString;
        }
    };
    
    // Handle row click to show details
    const handleRowClick = (result: SearchResult) => {
        setSelectedResult(result);
    };

    // Navigation to search history
    const handleShowSearchHistory = () => {
        navigate('/search-history');
    };

    // CSS for truncated text cells
    const truncatedCellStyle = {
        maxWidth: '150px',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };
    
    // Styles for details popup
    const labelStyle = {
        textAlign: 'right' as const,
        paddingRight: '15px',
        fontWeight: 'bold'
    };
    
    const urlValueStyle = {
        textAlign: 'left' as const,
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const
    };
    
    const detailsGridStyle = {
        display: 'grid',
        gridTemplateColumns: '180px auto',
        columnGap: '15px',
        rowGap: '10px',
        alignItems: 'baseline'
    };

    // Show loading while auth is loading
    if (!authLoaded) {
        return <div className="loading-indicator">Loading authentication...</div>;
    }

    return (
        <div className="web-search-container">
            <div className="web-search-header">
                <h1>Web Search</h1>
                <button 
                    onClick={handleShowSearchHistory}
                    className="web-search-history-button"
                >
                    <i className="fas fa-history" style={{ marginRight: '0.5rem' }}></i>
                    Search History
                </button>
            </div>

            {/* Search Form */}
            <div className="web-search-form">
                <h3>New Search</h3>
                
                <div className="web-search-form-fields">
                    {/* Query Field - Full Width */}
                    <div className="web-search-form-row">
                        <div className="web-search-form-group">
                            <label className="web-search-form-label">
                                Query *
                            </label>
                            <input 
                                type="text"
                                name="query"
                                value={newTarget.query}
                                onChange={handleInputChange}
                                placeholder="Enter your search query..."
                                className="web-search-form-input"
                            />
                        </div>
                    </div>

                    {/* Max Results Field - Full Width */}
                    <div className="web-search-form-row">
                        <div className="web-search-form-group">
                            <label className="web-search-form-label">
                                Max Results
                            </label>
                            <input 
                                type="number"
                                name="max_number_results"
                                value={newTarget.max_number_results}
                                onChange={handleInputChange}
                                min="1"
                                max="1000"
                                className="web-search-form-input"
                            />
                        </div>
                    </div>

                    {/* Issuer Field - Full Width */}
                    <div className="web-search-form-row">
                        <div className="web-search-form-group">
                            <label className="web-search-form-label">
                                Issuer
                            </label>
                            <input 
                                type="text"
                                name="issuer"
                                value={newTarget.issuer}
                                onChange={handleInputChange}
                                placeholder="Optional"
                                className="web-search-form-input"
                            />
                        </div>
                    </div>

                    {/* Name Field - Full Width */}
                    <div className="web-search-form-row">
                        <div className="web-search-form-group">
                            <label className="web-search-form-label">
                                Name
                            </label>
                            <input 
                                type="text"
                                name="name"
                                value={newTarget.name}
                                onChange={handleInputChange}
                                placeholder="Optional"
                                className="web-search-form-input"
                            />
                        </div>
                    </div>

                    {/* Country Field - Full Width */}
                    <div className="web-search-form-row">
                        <div className="web-search-form-group">
                            <label className="web-search-form-label">
                                Country
                            </label>
                            <select 
                                name="country"
                                value={newTarget.country}
                                onChange={handleInputChange}
                                className="web-search-form-select"
                            >
                                <option value="">Select Country (Optional)</option>
                                {countries.map(country => (
                                    <option key={country} value={country}>{country}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="web-search-form-submit">
                    <button 
                        onClick={handleSearch}
                        disabled={loading}
                        className="web-search-submit-button"
                    >
                        {loading ? (
                            <>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                                Searching...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-search" style={{ marginRight: '0.5rem' }}></i>
                                Search
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
                <div className="web-search-results">
                    <h3>Search Results</h3>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: '45%' }}>URL</th>
                                    <th style={{ width: '35%' }}>Query</th>
                                    <th style={{ width: '5%' }}>Rank</th>
                                    <th style={{ width: '5%' }}>Target Count</th>
                                    <th style={{ width: '10%' }}>Insert Timestamp</th>
                                    <th style={{ width: '5%' }}>PDF Check</th>
                                </tr>
                            </thead>
                            <tbody>
                                {searchResults.map(result => (
                                    <tr 
                                        key={result._id} 
                                        onClick={() => handleRowClick(result)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td style={{...truncatedCellStyle, maxWidth: '25vw'}} title={result.url}>{result.url}</td>
                                        <td style={{...truncatedCellStyle, maxWidth: '50vw'}} title={result.query}>{result.query}</td>
                                        <td style={{textAlign: 'center'}}>{result.index}</td>
                                        <td style={{textAlign: 'center'}}>{formatTargetIdDisplay(result.target_id)}</td>
                                        <td style={{whiteSpace: 'nowrap', textAlign: 'center'}}>{formatDate(result.insert_timestamp)}</td>
                                        <td style={{textAlign: 'center'}}>
                                            {result.checked_on_pdf_timestamp ?
                                                <i className="fas fa-check" style={{ color: 'green' }} title={`checked on: ${formatDate(result.checked_on_pdf_timestamp)}`}></i> :
                                                <i className="fas fa-times" style={{ color: 'red' }} title="not checked yet"></i>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {/* Pagination Information */}
                        {pagination && (
                            <div className="pagination-container">
                                <div className="pagination-info">
                                    Showing {searchResults.length} of {pagination.totalDocuments} search results
                                </div>
                                
                                {/* Pagination Controls */}
                                <div className="pagination-controls">
                                    <button 
                                        onClick={() => handlePageChange(1)} 
                                        disabled={!pagination.hasPreviousPage}
                                        className="pagination-button"
                                    >
                                        <i className="fas fa-angle-double-left"></i>
                                    </button>
                                    
                                    <button 
                                        onClick={() => handlePageChange(pagination.currentPage - 1)} 
                                        disabled={!pagination.hasPreviousPage}
                                        className="pagination-button"
                                    >
                                        <i className="fas fa-angle-left"></i>
                                    </button>
                                    
                                    <span className="pagination-page-info">Page {pagination.currentPage} of {pagination.totalPages}</span>
                                    
                                    <button 
                                        onClick={() => handlePageChange(pagination.currentPage + 1)} 
                                    disabled={!pagination.hasNextPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-right"></i>
                                </button>
                                
                                <button 
                                    onClick={() => handlePageChange(pagination.totalPages)} 
                                    disabled={!pagination.hasNextPage}
                                    className="pagination-button"
                                >
                                    <i className="fas fa-angle-double-right"></i>
                                </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <>
                {loading && searchResults.length === 0 && (
                    <div className="loading-indicator">Loading search results...</div>
                )}

                {!loading && searchResults.length === 0 && currentSearchTargetId && (
                    <div className="no-results">No search results found for this query.</div>
                )}
                
                {/* Details Popup */}
                {selectedResult && (
                    <div className="popup-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div className="popup-content" style={{
                        backgroundColor: 'white',
                        padding: '0',
                        borderRadius: '5px',
                        width: '700px',
                        maxWidth: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            backgroundColor: '#f0f0f0',
                            padding: '0.4rem 1rem',
                            borderTopLeftRadius: '5px',
                            borderTopRightRadius: '5px',
                            borderBottom: '1px solid #ddd'
                        }}>
                            <button
                                onClick={() => setSelectedResult(null)}
                                style={{ 
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.3rem',
                                    cursor: 'pointer',
                                    lineHeight: '1'
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <div style={{ padding: '1.5rem 2rem' }}>
                            <div style={{ ...detailsGridStyle, marginBottom: '20px' }}>
                                <div style={labelStyle}>URL:</div>
                                <div style={urlValueStyle}>
                                    <a href={selectedResult.url} target="_blank" rel="noopener noreferrer">{selectedResult.url}</a>
                                </div>
                                
                                <div style={labelStyle}>Rank:</div>
                                <div>{selectedResult.index}</div>
                                
                                <div style={labelStyle}>Query:</div>
                                <div>{selectedResult.query}</div>
                                
                                <div style={labelStyle}>Target IDs:</div>
                                <div>{formatTargetIdDisplay(selectedResult.target_id)}</div>
                                
                                <div style={labelStyle}>Checked PDF Time:</div>
                                <div>{formatDate(selectedResult.checked_on_pdf_timestamp)}</div>
                                
                                <div style={labelStyle}>Processed:</div>
                                <div>{selectedResult.processed === true ? 'Yes' : selectedResult.processed === false ? 'No' : selectedResult.processed}</div>
                                
                                <div style={labelStyle}>Insert Timestamp:</div>
                                <div>{formatDate(selectedResult.insert_timestamp)}</div>
                                
                                <div style={labelStyle}>Update Timestamp:</div>
                                <div>{formatDate(selectedResult.update_timestamp)}</div>
                                
                                <div style={labelStyle}>CUSIP:</div>
                                <div>{selectedResult.cusip || '-'}</div>
                                
                                <div style={labelStyle}>Has ISIN:</div>
                                <div>{selectedResult.has_isin ? 'Yes' : 'No'}</div>
                                
                                <div style={labelStyle}>ISIN:</div>
                                <div>{selectedResult.isin || '-'}</div>
                                
                                <div style={labelStyle}>Validated:</div>
                                <div>{selectedResult.validated ? 'Yes' : 'No'}</div>
                                
                                <div style={labelStyle}>Checking Failed:</div>
                                <div>{selectedResult.checking_failed ? 'Yes' : 'No'}</div>
                            </div>
                            
                            {/* PDF URLs Section */}
                            {selectedResult.pdf_urls && selectedResult.pdf_urls.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ marginBottom: '0.5rem', textAlign: 'left' }}>PDF URLs</h3>
                                    <ul style={{ paddingLeft: '1.5rem' }}>
                                        {selectedResult.pdf_urls.map((url, index) => (
                                            <li key={`pdf-${index}`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: '0.5rem' }}>
                                                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Prepare PDF URLs Section */}
                            {selectedResult.prepare_pdf_urls && selectedResult.prepare_pdf_urls.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ marginBottom: '0.5rem', textAlign: 'left' }}>Prepared PDF URLs</h3>
                                    <ul style={{ paddingLeft: '1.5rem' }}>
                                        {selectedResult.prepare_pdf_urls.map((url, index) => (
                                            <li key={`prep-pdf-${index}`} style={{ wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: '0.5rem' }}>
                                                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                                <button 
                                    onClick={() => setSelectedResult(null)}
                                    style={{ 
                                        padding: '0.5rem 1.5rem',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </>
        </div>
    );
};

export default WebSearchView; 